/**
 * Web push, from the page's side.
 *
 * The whole flow in one place because it is easy to get subtly wrong in ways
 * that only show up on someone else's phone:
 *
 * • **Permission is one-shot.** A browser will not re-prompt after a denial;
 *   the person has to dig through site settings. So we never call
 *   `requestPermission()` on load — only from an explicit tap, and only after
 *   the server has confirmed it actually has VAPID keys. Prompting on a
 *   deployment that cannot send is how a permission gets spent for nothing.
 *
 * • **iOS only delivers push to an INSTALLED PWA** (16.4+). In Safari proper,
 *   `Notification` and `PushManager` exist and `subscribe()` rejects. That
 *   reads as a bug unless the UI says "Add to Home Screen first", which is
 *   what `pushEnvironment()` is for.
 *
 * • **The browser's subscription is the truth, not our database.** A push
 *   service can rotate or drop an endpoint at any time, and the service worker
 *   cannot reliably tell the server when it does (no auth token in a worker).
 *   So the app re-syncs whatever the browser currently holds on every load.
 */

import api from '@monorepo/utils/apiRequest';

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const isIos = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch point count is what separates them.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/**
 * Why push may or may not be available here, in terms the UI can explain.
 * `blocked` is deliberately distinct from `unsupported`: one is fixable by the
 * person, and telling them which is the entire value of this function.
 */
export const pushEnvironment = () => {
  if (!pushSupported()) {
    return {
      available: false,
      reason: isIos() && !isStandalone() ? 'ios-not-installed' : 'unsupported',
    };
  }
  // Safari on iOS exposes the APIs but rejects subscribe() outside a home
  // screen install, so the capability check alone is not enough.
  if (isIos() && !isStandalone()) {
    return { available: false, reason: 'ios-not-installed' };
  }
  if (Notification.permission === 'denied') {
    return { available: false, reason: 'blocked' };
  }
  return { available: true, reason: null };
};

export const permissionState = () =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

const urlBase64ToUint8Array = (value) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
};

// `navigator.serviceWorker.ready` NEVER SETTLES when nothing is registered —
// it is not a rejection and not a timeout, it simply hangs forever. Awaiting
// it directly left the notification settings page blank on any load where the
// worker had not activated yet: first visit, registration blocked, or the dev
// server (where the worker is disabled on purpose). Race it.
const READY_TIMEOUT_MS = 3000;

const registration = async () => {
  if (!('serviceWorker' in navigator)) return null;
  // `ready` is still the one we want when it comes: it resolves only once a
  // worker is ACTIVE, which is what subscribing requires. `getRegistration()`
  // can hand back one that is still installing, so it is only the fallback.
  const timeout = new Promise((resolve) => {
    setTimeout(() => resolve(navigator.serviceWorker.getRegistration()), READY_TIMEOUT_MS);
  });
  try {
    return (await Promise.race([navigator.serviceWorker.ready, timeout])) || null;
  } catch (e) {
    return null;
  }
};

/** The VAPID public key and whether the deployment can send at all. */
export const fetchPushConfig = async () => {
  const res = await api.get('/push/vapid-public-key');
  return { publicKey: res?.publicKey || '', configured: !!res?.configured };
};

/**
 * Hand the worker what it needs to re-subscribe on its own.
 *
 * A `pushsubscriptionchange` fires with no page open, so the worker cannot ask
 * for this then — it has to already have it.
 */
const primeWorker = async (publicKey) => {
  const reg = await registration();
  const target = reg?.active || navigator.serviceWorker.controller;
  if (!target) return;
  target.postMessage({
    type: 'PUSH_CONFIG',
    config: {
      applicationServerKey: publicKey,
      apiBase: import.meta.env?.VITE_API_BASE_URL || '/api',
    },
  });
};

/** The subscription this browser currently holds, or null. */
export const currentSubscription = async () => {
  const reg = await registration();
  if (!reg || !reg.pushManager) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch (e) {
    return null;
  }
};

/**
 * Turn notifications on. Must be called from a user gesture.
 *
 * Returns { ok, reason } rather than throwing on a refusal: a declined
 * permission prompt is an answer, not an error, and the caller has to render
 * something either way.
 */
export const enablePush = async () => {
  const env = pushEnvironment();
  if (!env.available) return { ok: false, reason: env.reason };

  const { publicKey, configured } = await fetchPushConfig();
  if (!configured || !publicKey) return { ok: false, reason: 'not-configured' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await registration();
  if (!reg) return { ok: false, reason: 'unsupported' };

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        // Required: every major browser refuses a silent push subscription.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      // The commonest cause by far is iOS Safari outside a home-screen
      // install, which we have already screened for — so anything landing
      // here is worth naming rather than swallowing.
      return { ok: false, reason: 'subscribe-failed', error: e?.message };
    }
  }

  await api.post('/push/subscribe', { subscription: subscription.toJSON() });
  await primeWorker(publicKey);
  return { ok: true };
};

/** Turn them off on THIS browser. Other devices keep theirs. */
export const disablePush = async () => {
  const subscription = await currentSubscription();
  if (!subscription) return { ok: true };

  const { endpoint } = subscription.toJSON();
  // Tell the server first: if unsubscribing locally succeeded and the call
  // then failed, the server would keep pushing to a dead endpoint until it
  // 410s, and this browser would have no record left to retry the delete with.
  try {
    await api.post('/push/unsubscribe', { endpoint });
  } catch (e) {
    /* a 410 will clean it up server-side anyway */
  }
  await subscription.unsubscribe();
  return { ok: true };
};

/**
 * Reconcile the browser's subscription with the server's copy. Safe to call
 * on every load; does nothing unless permission is already granted.
 *
 * This is the real fix for endpoint rotation. The service worker's
 * `pushsubscriptionchange` handler tries too, but it has no auth token, so
 * this is the path that is guaranteed to be authenticated.
 */
export const syncSubscription = async () => {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const subscription = await currentSubscription();
    if (!subscription) return false;
    await api.post('/push/subscribe', { subscription: subscription.toJSON() });
    const { publicKey } = await fetchPushConfig();
    if (publicKey) await primeWorker(publicKey);
    return true;
  } catch (e) {
    return false;
  }
};

export const getPreferences = () => api.get('/push/preferences');
export const savePreferences = (payload) => api.put('/push/preferences', payload);
export const sendTestPush = () => api.post('/push/test', {});
export const listDevices = () => api.get('/push/devices');
